import React from 'react';
import {
    CheckCircle,
    AlertCircle,
    AlertTriangle,
    Info,
    CheckCircle2
} from 'lucide-react';
import './MonitoringWidgets.css';

import type { AnalyticsKeyLocation, AnalyticsActionableInsight } from '../../services/api';
import './MonitoringWidgets.css';

interface LocationsProps {
    data?: AnalyticsKeyLocation[];
}

export const KeyLocations: React.FC<LocationsProps> = ({ data = [] }) => {
    // Calculate out of max count to keep bars relative 
    const maxCount = Math.max(...(data.map(d => d.count)), 1);

    return (
        <div className="monitoring-widget">
            <h3 className="widget-title">Key Locations</h3>
            <div className="location-list">
                {data.map((loc, i) => {
                    const percentage = (loc.count / maxCount) * 100;
                    return (
                        <div key={loc.city} className="location-item">
                            <div className="location-info">
                                <span className="location-rank">{i + 1}</span>
                                <span className="location-name">{loc.city}</span>
                                <span className="location-value">{loc.count.toLocaleString()}</span>
                            </div>
                            <div className="location-progress-bg">
                                <div
                                    className="location-progress-bar"
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const SystemHealth: React.FC = () => {
    const statusItems = [
        { label: 'Card Processing System', status: 'Healthy', color: '#10b981' },
        { label: 'Payment Gateway', status: 'Online', color: '#10b981' },
        { label: 'Fuel Terminal Region', status: 'Syncing', color: '#3b82f6' },
        { label: 'Last Data Sync', status: 'Synced', color: '#8b5cf6', subtext: 'Last updated 5 mins ago' },
    ];

    return (
        <div className="monitoring-widget">
            <h3 className="widget-title">System Status & Health</h3>
            <div className="status-grid">
                {statusItems.map((item) => (
                    <div key={item.label} className="status-item">
                        <div className="status-item-left">
                            <div className="status-indicator-icon" style={{ color: item.color, backgroundColor: `${item.color}10` }}>
                                <CheckCircle size={14} />
                            </div>
                            <div className="status-labels">
                                <span className="status-label">{item.label}</span>
                                {item.subtext && <span className="status-subtext">{item.subtext}</span>}
                            </div>
                        </div>
                        <div className="status-badge" style={{ color: item.color, backgroundColor: `${item.color}10` }}>
                            {item.status}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface ActionableInsightsProps {
    data?: AnalyticsActionableInsight[];
}

export const ActionableInsights: React.FC<ActionableInsightsProps> = ({ data = [] }) => {
    const getTypeConfig = (type: string) => {
        switch (type) {
            case 'warning': return { icon: AlertTriangle, color: '#f59e0b', bg: '#fff7ed' };
            case 'error': return { icon: AlertCircle, color: '#ef4444', bg: '#fef2f2' };
            case 'info': return { icon: Info, color: '#3b82f6', bg: '#eff6ff' };
            case 'success': return { icon: CheckCircle2, color: '#10b981', bg: '#ecfdf5' };
            default: return { icon: Info, color: '#6b7280', bg: '#f3f4f6' };
        }
    };

    return (
        <div className="insights-widget">
            <div className="widget-header">
                <h3 className="widget-title">Actionable Insights</h3>
            </div>
            <div className="insights-list">
                {data.length === 0 && <p style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>No insights available at this time.</p>}
                {data.map((insight, i) => {
                    const config = getTypeConfig(insight.type);
                    const Icon = config.icon;
                    return (
                        <div key={i} className="insight-card" style={{ backgroundColor: config.bg, borderLeft: `4px solid ${config.color}` }}>
                            <Icon size={18} style={{ color: config.color }} />
                            <p className="insight-text">{insight.text}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
